import React, { useState } from 'react';
import {
  Waves,
  Bed,
  Droplets,
  Sparkles,
  MessageSquareHeart,
  Play,
  RotateCcw,
  CheckCircle2,
  Clock,
  ShieldAlert,
  Moon,
  Footprints,
  Radio,
  FileCheck,
  Copy,
  Check,
  DollarSign,
  Scale,
  Heart,
  CalendarCheck,
  Signal,
  AlertCircle,
  Refrigerator,
  Pill,
  Activity,
  PhoneCall,
  Volume2,
  Mic,
  Smile,
  Pause,
  Bot,
  Gauge,
  PhoneForwarded,
  Sliders,
  AudioLines,
  Smartphone,
  Grid,
  ArrowRight
} from 'lucide-react';
import { AmbientRoom, FamilyDigestSubject } from '../types';
import { generateAmbientProactiveIntervention } from '../services/geminiService';
import { playFallAlertSound, playVoiceBioAudio, stopVoiceBioAudio } from '../services/audioService';
import { NativeMarkdownViewer } from './NativeMarkdownViewer';
import { AqaraHardwareModal } from './AqaraHardwareModal';
import { EmergencySmsModal } from './EmergencySmsModal';

interface AmbientSafetyDashboardProps {
  rooms: AmbientRoom[];
  onSimulateFall: () => void;
  onTriggerHardwareFall?: (roomId: string) => void;
  onSimulateBedExit: () => void;
  onSimulateUTISpike: () => void;
  onResetSimulation: () => void;
  onOpenFamilyDigest: (subject: FamilyDigestSubject) => void;
  onUpdateRoomSbar: (roomId: string, note: string) => void;
}

export const AmbientSafetyDashboard: React.FC<AmbientSafetyDashboardProps> = ({
  rooms,
  onSimulateFall,
  onTriggerHardwareFall,
  onSimulateBedExit,
  onSimulateUTISpike,
  onResetSimulation,
  onOpenFamilyDigest,
  onUpdateRoomSbar
}) => {
  const [analyzingRoomId, setAnalyzingRoomId] = useState<string | null>(null);
  const [copiedSbarRoomId, setCopiedSbarRoomId] = useState<string | null>(null);
  const [dispatchedSbarRoomId, setDispatchedSbarRoomId] = useState<string | null>(null);
  const [isAqaraModalOpen, setIsAqaraModalOpen] = useState<boolean>(false);
  const [aqaraModalTab, setAqaraModalTab] = useState<'guide' | 'webhook' | 'test' | 'calibration'>('calibration');
  const [selectedCalibratedRoomId, setSelectedCalibratedRoomId] = useState<string>('101');
  const [isSmsModalOpen, setIsSmsModalOpen] = useState<boolean>(false);
  const [emergencyPhone, setEmergencyPhone] = useState<string>(() => {
    return localStorage.getItem('medicore_emergency_phone') || '+1 (617) 555-0144';
  });
  const [heartbeatPinging, setHeartbeatPinging] = useState<boolean>(false);
  const [heartbeatToast, setHeartbeatToast] = useState<string | null>(null);

  // Voice playback and callback state
  const [playingAudioRoomId, setPlayingAudioRoomId] = useState<string | null>(null);
  const [playbackSeconds, setPlaybackSeconds] = useState<number>(0);
  const [callbackTriggeredRoomId, setCallbackTriggeredRoomId] = useState<string | null>(null);

  const handleTriggerHeartbeatPing = async () => {
    setHeartbeatPinging(true);
    try {
      const res = await fetch('https://us-central1-rugged-alcove-388517.cloudfunctions.net/aqaraRadarWebhook?key=mc-radar-sec-7f9a2b4e8c1d3e5f', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomNumber: '101',
          event: 'heartbeat',
          deviceId: 'aqara-fp2-room101',
          rssi: '-50 dBm (Strong)'
        })
      });
      if (res.ok) {
        setHeartbeatToast('🟢 Sensor Health Watchdog ACK: 60GHz Telemetry verified healthy (-50 dBm RSSI, Room 101)');
        setTimeout(() => setHeartbeatToast(null), 4000);
      }
    } catch (e) {
      //
    } finally {
      setHeartbeatPinging(false);
    }
  };

  const isCaregiverDispatched = (r: AmbientRoom) => r.activeAlert.startsWith('Caregiver Dispatched');
  const activeAlertRooms = rooms.filter((r) => r.activeAlert !== 'None' && !isCaregiverDispatched(r));
  const dispatchedRooms = rooms.filter((r) => isCaregiverDispatched(r));

  const handleAnalyzeWithGemini = async (room: AmbientRoom) => {
    setAnalyzingRoomId(room.roomId);
    const note = await generateAmbientProactiveIntervention(room);
    onUpdateRoomSbar(room.roomId, note);
    setAnalyzingRoomId(null);
  };

  const handleCopySbar = (roomId: string, note: string) => {
    navigator.clipboard.writeText(note);
    setCopiedSbarRoomId(roomId);
    setTimeout(() => setCopiedSbarRoomId(null), 2000);
  };

  const handleDispatchSbar = (roomId: string) => {
    setDispatchedSbarRoomId(roomId);
    setTimeout(() => setDispatchedSbarRoomId(null), 3000);
  };

  // Web Audio & Media Alert Chime for Fall Detection & Trauma Sirens
  const playAlertChime = () => {
    playFallAlertSound();
  };

  // Play Audible Voice Bio Speech & Recorded Acoustic Bio
  const handleTogglePlayAudio = (room: AmbientRoom) => {
    if (playingAudioRoomId === room.roomId) {
      stopVoiceBioAudio();
      setPlayingAudioRoomId(null);
      setPlaybackSeconds(0);
    } else {
      setPlayingAudioRoomId(room.roomId);
      setPlaybackSeconds(0);

      playVoiceBioAudio(
        room.residentName,
        (sec) => {
          setPlaybackSeconds(sec);
        },
        () => {
          setPlayingAudioRoomId(null);
          setPlaybackSeconds(0);
        }
      );
    }
  };

  // Trigger Manual Caregiver Callback simulation
  const handleTriggerCaregiverCallback = (room: AmbientRoom) => {
    setCallbackTriggeredRoomId(room.roomId);
    setTimeout(() => {
      setCallbackTriggeredRoomId(null);
      alert(`Manual Caregiver Callback dispatched to on-duty nurse for ${room.roomNumber} (${room.residentName}). VoIP audio bridge initiated.`);
    }, 1800);
  };

  const handleLaunchFamilySMS = (room: AmbientRoom) => {
    const fridgeText = room.adlActivity?.fridgeOpened || 'Opened 8:15 AM (Breakfast Confirmed)';
    const medText = room.adlActivity?.medCabinetOpened || 'Opened 9:02 AM (Dose Adherent)';
    const bpText = room.rpmTelemetry?.cellularBP || '128/82 mmHg';
    const deltaText = room.rpmTelemetry?.weightDelta48hLbs
      ? `${room.rpmTelemetry.weightDelta48hLbs > 0 ? '+' : ''}${room.rpmTelemetry.weightDelta48hLbs} lbs in 48h`
      : 'stable';
    const voiceNote = room.morningWellnessCall?.sentimentMood || 'Tired but in good spirits';

    const subject: FamilyDigestSubject = {
      id: `room-${room.roomId}`,
      name: room.residentName,
      recipientName: room.familyContact.name,
      recipientRelation: room.familyContact.relation,
      recipientPhone: room.familyContact.phone,
      sleepScoreOrState: `${room.bedTimeLabel} (${room.bedState})`,
      vitalsSummary: `Cellular BP: ${bpText}, Weight delta: ${deltaText}, Voice Sentiment: "${voiceNote}"`,
      nutritionActivitySummary: `Meal Prep: ${fridgeText}, Med Cabinet: ${medText}, Morning Call completed at ${room.morningWellnessCall?.callTimestamp || '8:30 AM'}`,
      locationOrRoom: `${room.roomNumber} (${room.wing})`
    };
    onOpenFamilyDigest(subject);
  };

  const totalQualifiedCount = rooms.filter((r) => r.rpmTelemetry?.isQualified).length;
  const totalReimbursementProjected = rooms
    .filter((r) => r.rpmTelemetry?.isQualified)
    .reduce((sum, r) => sum + (r.rpmTelemetry?.reimbursementAmount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Top Interactive Simulation Bar */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-900 to-indigo-950/40 border-2 border-indigo-500/40 rounded-2xl p-4 shadow-xl">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-indigo-500/20 border border-indigo-500/40 flex items-center justify-center text-indigo-300">
              <Play className="h-4 w-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-white text-xs sm:text-sm tracking-wide">
                  Live Hardware Demonstration & Simulation Bar
                </span>
                <span className="bg-indigo-500/20 text-indigo-300 text-[10px] font-mono px-2 py-0.5 rounded font-semibold">
                  INTERACTIVE LAB
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Trigger simulated live sensor telemetry events before physical hardware installation.
              </p>
            </div>
          </div>

          {/* Action Simulation Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                playAlertChime();
                onSimulateFall();
              }}
              className="px-3 py-1.5 rounded-xl bg-rose-600/30 hover:bg-rose-600/50 text-rose-200 border border-rose-500/50 text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95"
            >
              <ShieldAlert className="h-3.5 w-3.5 text-rose-400 animate-pulse" />
              <span>Simulate Fall (Room 101)</span>
            </button>

            <button
              onClick={() => {
                playAlertChime();
              }}
              className="px-3 py-1.5 rounded-xl bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-200 border border-indigo-500/50 text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95"
              title="Test hospital alert alarm chime audio directly"
            >
              <Volume2 className="h-3.5 w-3.5 text-indigo-400" />
              <span>Test Audio Alarm Chime</span>
            </button>

            <button
              onClick={onSimulateBedExit}
              className="px-3 py-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95"
            >
              <Moon className="h-3.5 w-3.5 text-amber-400" />
              <span>Simulate Bed Exit (Room 102)</span>
            </button>

            <button
              onClick={onSimulateUTISpike}
              className="px-3 py-1.5 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-200 border border-cyan-500/40 text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95"
            >
              <Droplets className="h-3.5 w-3.5 text-cyan-400" />
              <span>Simulate UTI Alert (Room 103)</span>
            </button>

            <button
              onClick={onResetSimulation}
              className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-xs font-medium transition flex items-center gap-1"
              title="Reset all rooms to normal baseline"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              <span>Reset Grid</span>
            </button>

            <button
              onClick={handleTriggerHeartbeatPing}
              disabled={heartbeatPinging}
              className="px-3 py-1.5 rounded-xl bg-emerald-600/25 hover:bg-emerald-600/40 text-emerald-300 border border-emerald-500/50 text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95 disabled:opacity-50"
              title="Ping 60GHz radar keepalive to verify sensor health watchdog"
            >
              <Activity className={`h-3.5 w-3.5 text-emerald-400 ${heartbeatPinging ? 'animate-spin' : ''}`} />
              <span>{heartbeatPinging ? 'Pinging...' : 'Test Sensor Heartbeat'}</span>
            </button>

            <button
              onClick={() => setIsSmsModalOpen(true)}
              className="px-3 py-1.5 rounded-xl bg-indigo-600/30 hover:bg-indigo-600/50 text-indigo-200 border border-indigo-500/50 text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95"
              title="Configure emergency caregiver mobile phone number for real-time automated voice call and cellular dispatch"
            >
              <PhoneCall className="h-3.5 w-3.5 text-emerald-400 animate-pulse" />
              <span>Emergency Voice &amp; SMS Dispatch</span>
            </button>

            <button
              onClick={() => {
                setAqaraModalTab('calibration');
                setIsAqaraModalOpen(true);
              }}
              className="px-3 py-1.5 rounded-xl bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-200 border border-cyan-500/50 text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95"
              title="Calibrate 60GHz mmWave radar zones (Bed, Floor Impact, Bathroom, Exclusion) & height altitude"
            >
              <Grid className="h-3.5 w-3.5 text-cyan-400" />
              <span>Calibrate 60GHz Radar Zones</span>
            </button>

            <button
              onClick={() => {
                setAqaraModalTab('webhook');
                setIsAqaraModalOpen(true);
              }}
              className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 text-xs font-bold transition flex items-center gap-1.5 shadow-md shadow-teal-500/20 active:scale-95"
            >
              <Radio className="h-3.5 w-3.5 animate-pulse" />
              <span>Connect Aqara FP2 Hardware</span>
            </button>

            {/* Live Cloud Bridge Active Indicator with Security Badge */}
            <div className="flex items-center gap-2 px-3 py-1 rounded-xl bg-emerald-950/50 border border-emerald-500/50 text-emerald-300 text-[11px] font-mono shadow-sm">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="font-semibold">Radar Bridge: Active</span>
              <span className="text-[9px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 rounded border border-emerald-500/40 font-bold uppercase">
                API Key Secured
              </span>
            </div>
          </div>
        </div>

        {/* Heartbeat Feedback Toast */}
        {heartbeatToast && (
          <div className="mt-3 px-3 py-2 rounded-xl bg-emerald-950/60 border border-emerald-500/50 text-emerald-300 text-xs font-mono flex items-center gap-2 animate-fadeIn">
            <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
            <span>{heartbeatToast}</span>
          </div>
        )}
      </div>

      {/* Caregiver dispatched confirmation (shown after keypress 1) */}
      {dispatchedRooms.length > 0 && (
        <div className="p-4 rounded-2xl bg-emerald-950/40 border-2 border-emerald-500 shadow-xl flex items-center gap-3">
          <div className="p-2 rounded-xl bg-emerald-500 text-slate-950">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <div>
            <span className="font-extrabold text-emerald-300 text-sm tracking-wide">
              CAREGIVER DISPATCHED: ALARM ACKNOWLEDGED
            </span>
            <p className="text-xs text-emerald-200 mt-0.5">
              {dispatchedRooms.map((r) => `${r.roomNumber} (${r.residentName}): bedside caregiver en route`).join(' • ')}
            </p>
          </div>
        </div>
      )}

      {/* Emergency Active Alert Banner if any alert is active */}
      {activeAlertRooms.length > 0 && (
        <div className="p-4 rounded-2xl bg-rose-950/40 border-2 border-rose-500 shadow-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-rose-500 text-slate-950 font-black">
              <ShieldAlert className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-extrabold text-rose-300 text-sm tracking-wide">
                  ACTIVE AMBIENT SAFETY ALERTS ({activeAlertRooms.length} ROOMS)
                </span>
                <span className="bg-rose-500 text-slate-950 font-black text-[9px] px-2 py-0.5 rounded font-mono">
                  ACTION REQUIRED
                </span>
              </div>
              <p className="text-xs text-rose-200 mt-0.5">
                {activeAlertRooms.map((r) => `${r.roomNumber} (${r.residentName} - ${r.activeAlert})`).join(' • ')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            <button
              onClick={() => {
                if (activeAlertRooms[0]) {
                  handleAnalyzeWithGemini(activeAlertRooms[0]);
                }
              }}
              disabled={analyzingRoomId !== null}
              className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-slate-950 font-bold text-xs transition flex items-center gap-1.5 shadow-md shadow-teal-500/20 disabled:opacity-50"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>Analyze Alert with Gemini</span>
            </button>
          </div>
        </div>
      )}

      {/* Room-by-Room Grid Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Radio className="h-5 w-5 text-teal-400" />
            <span>Live Ambient Telemetry Dashboard (Room-by-Room Grid)</span>
          </h2>
          <p className="text-xs text-slate-400">
            Real-time 60 GHz mmWave radar postures, micro-vibration bed presence, bathroom counters, and continuous gait velocity tracking.
          </p>
        </div>
        <span className="text-xs font-mono text-slate-400">
          Showing {rooms.length} Suites Monitored
        </span>
      </div>

      {/* Room Cards Grid (Room 101, Room 102, Room 103) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {rooms.map((room) => {
          const isFall = room.posture === 'Fall Alert' || room.activeAlert === 'Fall Event';
          const isBedExitAlert = room.activeAlert === 'Night Bed Exit';
          const isUTISpike = room.bathroomVisitsToday > 6 || room.activeAlert === 'UTI Spike';
          const isGaitSlowing = room.gaitSpeedMs < 0.8;
          const hasActiveAlert = room.activeAlert !== 'None';

          const isFridgeAlert = room.adlActivity?.fridgeAlert || room.roomId === '103';
          const fridgeStatus = room.adlActivity?.fridgeOpened || (room.roomId === '103'
            ? 'Inactive since 6:00 PM yesterday (Missed Meal Warning)'
            : 'Opened 8:15 AM (Breakfast Confirmed)');
          const medStatus = room.adlActivity?.medCabinetOpened || 'Opened 9:02 AM (Dose Adherent)';

          const callData = room.morningWellnessCall;
          const isAudioPlaying = playingAudioRoomId === room.roomId;
          const isCallbackTriggered = callbackTriggeredRoomId === room.roomId;

          return (
            <div
              key={room.roomId}
              className={`rounded-3xl border-2 p-5 transition relative overflow-hidden flex flex-col justify-between shadow-xl ${
                isFall
                  ? 'bg-rose-950/50 border-rose-500 shadow-rose-950/60 ring-2 ring-rose-500/60'
                  : isBedExitAlert
                  ? 'bg-amber-950/30 border-amber-500/80 shadow-amber-950/30'
                  : isUTISpike
                  ? 'bg-slate-900 border-amber-500/60'
                  : 'bg-slate-900/80 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div>
                {/* Room Card Top Header */}
                <div className="flex items-start justify-between pb-3 border-b border-slate-800">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-extrabold text-base text-white font-mono tracking-wide">
                        {room.roomNumber}
                      </span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-teal-300 font-semibold">
                        {room.wing}
                      </span>
                    </div>
                    <h3 className="font-bold text-slate-100 text-sm mt-1">
                      {room.residentName} <span className="text-xs font-normal text-slate-400">({room.age}y)</span>
                    </h3>
                    <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">
                      {room.diagnosis}
                    </p>
                  </div>

                  {/* Active Alert Badge */}
                  <div className="flex flex-col items-end gap-1">
                    <span
                      className={`text-[10px] font-mono px-2.5 py-1 rounded-xl font-black uppercase tracking-wider ${
                        isFall
                          ? 'bg-rose-500 text-slate-950 animate-bounce'
                          : isBedExitAlert
                          ? 'bg-amber-400 text-slate-950 animate-pulse'
                          : isUTISpike
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                          : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                      }`}
                    >
                      {room.activeAlert !== 'None' ? room.activeAlert : 'Normal'}
                    </span>
                    <span className="text-[10px] font-mono text-slate-500">
                      Sub-mm Radar
                    </span>
                  </div>
                </div>

                {/* 4 Core Ambient Safety Cards */}
                <div className="space-y-2.5 mt-4">
                  {/* 1. mmWave Radar Posture */}
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between ${
                      isFall
                        ? 'bg-rose-950/80 border-rose-500 text-rose-200'
                        : 'bg-slate-950/80 border-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`p-2 rounded-xl ${isFall ? 'bg-rose-500 text-slate-950' : 'bg-slate-900 text-cyan-400'}`}>
                        <Waves className="h-4 w-4" />
                      </div>
                      <div>
                        <span className="text-[10px] font-mono uppercase text-slate-400 block">
                          mmWave Radar Posture
                        </span>
                        <span className={`text-xs font-bold ${isFall ? 'text-rose-300 font-extrabold text-sm animate-pulse' : 'text-slate-100'}`}>
                          {room.posture}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-slate-500">FMCW 60GHz</span>
                      <button
                        onClick={() => {
                          setSelectedCalibratedRoomId(room.roomId);
                          setAqaraModalTab('calibration');
                          setIsAqaraModalOpen(true);
                        }}
                        className="px-2 py-0.5 rounded-lg bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/40 text-[10px] font-mono font-bold transition flex items-center gap-1 shadow-sm active:scale-95"
                        title={`Open 8x8 Spatial Radar Grid & Zone Calibration for ${room.roomNumber}`}
                      >
                        <Grid className="h-3 w-3 text-teal-400" />
                        <span>Zone Map</span>
                        <ArrowRight className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  </div>

                  {/* 2. Bed Presence Timer */}
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between ${
                      isBedExitAlert
                        ? 'bg-amber-950/50 border-amber-500 text-amber-200'
                        : 'bg-slate-950/80 border-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`p-2 rounded-xl ${isBedExitAlert ? 'bg-amber-400 text-slate-950' : 'bg-slate-900 text-indigo-400'}`}>
                        <Bed className="h-4 w-4" />
                      </div>
                      <div>
                        <span className="text-[10px] font-mono uppercase text-slate-400 block">
                          Bed Presence Timer
                        </span>
                        <span className="text-xs font-bold text-slate-100">
                          {room.bedTimeLabel}
                        </span>
                      </div>
                    </div>
                    <span className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded ${
                      room.bedState === 'In Bed'
                        ? 'bg-emerald-500/10 text-emerald-300'
                        : 'bg-amber-500/10 text-amber-300'
                    }`}>
                      {room.bedState}
                    </span>
                  </div>

                  {/* 3. Bathroom Frequency Tracker */}
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between ${
                      isUTISpike
                        ? 'bg-amber-950/40 border-amber-500'
                        : 'bg-slate-950/80 border-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`p-2 rounded-xl ${isUTISpike ? 'bg-amber-500 text-slate-950' : 'bg-slate-900 text-amber-400'}`}>
                        <Droplets className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono uppercase text-slate-400">
                            Bathroom Visits Today
                          </span>
                          {isUTISpike && (
                            <span className="bg-amber-400 text-slate-950 text-[9px] font-bold px-1.5 py-0.2 rounded">
                              Suspected UTI Risk
                            </span>
                          )}
                        </div>
                        <span className={`text-xs font-mono font-bold ${isUTISpike ? 'text-amber-300 text-sm' : 'text-slate-100'}`}>
                          {room.bathroomVisitsToday} visits today
                        </span>
                      </div>
                    </div>
                    <span className="text-[10px] text-slate-400 font-mono">Last: {room.lastBathroomTime}</span>
                  </div>

                  {/* 4. Gait & Velocity Index */}
                  <div
                    className={`p-3 rounded-2xl border flex items-center justify-between ${
                      isGaitSlowing
                        ? 'bg-rose-950/20 border-rose-500/40'
                        : 'bg-slate-950/80 border-slate-800'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <div className={`p-2 rounded-xl ${isGaitSlowing ? 'bg-rose-500/20 text-rose-400' : 'bg-slate-900 text-teal-400'}`}>
                        <Footprints className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono uppercase text-slate-400">
                            Gait & Velocity Index
                          </span>
                          {isGaitSlowing && (
                            <span className="bg-rose-500/20 text-rose-400 border border-rose-500/30 text-[9px] font-mono font-bold px-1.5 py-0.2 rounded">
                              High Fall Risk
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-mono font-bold text-slate-100">
                          {room.gaitSpeedMs} m/s{' '}
                          <span className={`text-[11px] font-normal ${isGaitSlowing ? 'text-rose-400 font-semibold' : 'text-slate-400'}`}>
                            ({room.gaitStatus})
                          </span>
                        </span>
                      </div>
                    </div>
                    <span className="text-[10px] font-mono text-slate-500">Ref: 1.1 m/s</span>
                  </div>
                </div>

                {/* ADL ENVIRONMENTAL HABITS ROW */}
                <div className="mt-3.5 p-3 rounded-2xl bg-slate-950/90 border border-slate-800/90">
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase text-slate-400 mb-2">
                    <span className="flex items-center gap-1.5 font-bold text-slate-300">
                      <Activity className="h-3.5 w-3.5 text-teal-400" />
                      <span>ADL Environmental Habits</span>
                    </span>
                    <span className="text-teal-400 text-[9px] font-semibold">Real-Time Contact Sensors</span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    {/* Badge 1: Meal Prep (Fridge) */}
                    <div
                      className={`flex flex-col justify-between p-2.5 rounded-xl border transition ${
                        isFridgeAlert
                          ? 'bg-amber-950/40 border-amber-500/80 text-amber-200'
                          : 'bg-slate-900/90 border-slate-800 text-slate-300'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
                          <Refrigerator className={`h-3.5 w-3.5 ${isFridgeAlert ? 'text-amber-400' : 'text-cyan-400'}`} />
                          <span>Meal Prep (Fridge):</span>
                        </span>
                        {isFridgeAlert && (
                          <span className="bg-amber-400 text-slate-950 font-black text-[9px] px-1.5 py-0.2 rounded font-mono uppercase">
                            WARNING
                          </span>
                        )}
                      </div>
                      <span
                        className={`font-mono text-[11px] font-bold px-2 py-0.5 rounded-lg inline-block ${
                          isFridgeAlert
                            ? 'text-amber-300 bg-amber-950/80 border border-amber-500/60'
                            : 'text-cyan-300 bg-cyan-950/40 border border-cyan-800/40'
                        }`}
                      >
                        {fridgeStatus}
                      </span>
                    </div>

                    {/* Badge 2: Medication Cabinet */}
                    <div className="flex flex-col justify-between p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 text-slate-300">
                      <div className="flex items-center justify-between gap-1 mb-1">
                        <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-400">
                          <Pill className="h-3.5 w-3.5 text-emerald-400" />
                          <span>Medication Cabinet:</span>
                        </span>
                        <span className="text-[9px] font-mono text-emerald-400 bg-emerald-950/50 px-1 rounded border border-emerald-800/40">
                          RFID LOGGED
                        </span>
                      </div>
                      <span className="font-mono text-[11px] font-bold text-emerald-300 bg-emerald-950/40 border border-emerald-800/40 px-2 py-0.5 rounded-lg inline-block">
                        {medStatus}
                      </span>
                    </div>
                  </div>
                </div>

                {/* AI MORNING WELLNESS CALL & VOICE BIOMARKERS SECTION */}
                {callData && (
                  <div className="mt-3.5 p-3.5 rounded-2xl bg-gradient-to-br from-slate-950 via-slate-950 to-indigo-950/40 border-2 border-indigo-500/40 text-xs space-y-3 relative overflow-hidden">
                    {/* Top Call Record Bar */}
                    <div className="flex items-center justify-between border-b border-slate-800/80 pb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-indigo-500/20 text-indigo-400 border border-indigo-500/40">
                          <Mic className="h-4 w-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-white text-xs tracking-wide">
                              AI Morning Wellness Call & Voice Biomarkers
                            </span>
                            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                          </div>
                          <span className="text-[10px] text-slate-400 font-mono">
                            Time: <strong className="text-slate-200">{callData.callTimestamp}</strong> • Duration: <strong className="text-slate-200">{callData.duration}</strong> • Status: <strong className="text-emerald-400">{callData.status}</strong>
                          </span>
                        </div>
                      </div>

                      <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 font-bold uppercase">
                        GEMINI VOICE AGENT
                      </span>
                    </div>

                    {/* Acoustic Voice Biomarker Gauges (Vocal Energy, Speech Latency, Sentiment) */}
                    <div>
                      <div className="text-[10px] font-mono uppercase text-slate-400 mb-1.5 flex items-center justify-between">
                        <span className="flex items-center gap-1 font-bold text-slate-300">
                          <Sliders className="h-3 w-3 text-cyan-400" />
                          <span>Acoustic Voice Biomarker Gauges</span>
                        </span>
                        <span className="text-teal-400">Calibrated Baseline</span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {/* Gauge 1: Vocal Energy (Normal: 84%) */}
                        <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex flex-col justify-between">
                          <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 mb-1">
                            <span>Vocal Energy</span>
                            <span className="text-cyan-300 font-bold">{callData.vocalEnergyLabel}</span>
                          </div>
                          <div className="w-full bg-slate-950 h-2 rounded-full overflow-hidden border border-slate-800">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-400 transition-all duration-500"
                              style={{ width: `${callData.vocalEnergyPct}%` }}
                            />
                          </div>
                          <div className="text-[9px] text-slate-500 font-mono mt-1 text-right">
                            Amplitude: {callData.vocalEnergyPct}/100
                          </div>
                        </div>

                        {/* Gauge 2: Speech Latency / Pauses (Mild Delay - 1.8s) */}
                        <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex flex-col justify-between">
                          <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 mb-1">
                            <span>Speech Latency / Pauses</span>
                            <span className="text-amber-300 font-bold">{callData.speechLatencySecs}s</span>
                          </div>
                          <div className="w-full bg-slate-950 h-2 rounded-full overflow-hidden border border-slate-800">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-teal-400 via-amber-400 to-rose-400 transition-all duration-500"
                              style={{ width: `${Math.min(100, (callData.speechLatencySecs / 3.0) * 100)}%` }}
                            />
                          </div>
                          <div className="text-[9px] text-amber-300 font-mono mt-1 font-semibold">
                            {callData.speechLatencyLabel}
                          </div>
                        </div>

                        {/* Gauge 3: Sentiment / Mood ('Tired but in good spirits') */}
                        <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 flex flex-col justify-between">
                          <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 mb-0.5">
                            <span className="flex items-center gap-1">
                              <Smile className="h-3 w-3 text-pink-400" />
                              <span>Sentiment / Mood</span>
                            </span>
                            <span className="text-pink-400 font-mono text-[9px] font-bold">DSP AI</span>
                          </div>
                          <div className="text-xs font-bold text-slate-100 italic mt-0.5">
                            "{callData.sentimentMood}"
                          </div>
                          <div className="text-[9px] text-slate-500 font-mono mt-0.5">
                            Jitter: {callData.jitterShimmerIndex || '0.88%'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Short Conversational Dialogue Snippet between Gemini & Senior */}
                    <div className="p-3 rounded-xl bg-slate-900/90 border border-slate-800 text-[11px] space-y-2">
                      <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 border-b border-slate-800/80 pb-1">
                        <span className="flex items-center gap-1.5 font-bold text-slate-300">
                          <Bot className="h-3.5 w-3.5 text-teal-400" />
                          <span>Gemini AI Voice Agent Dialogue Snippet:</span>
                        </span>
                        <span className="text-indigo-400 font-semibold">Native Audio Stream</span>
                      </div>

                      <div className="space-y-1.5 pt-0.5">
                        {callData.transcriptSnippet.map((turn, tIdx) => (
                          <div key={tIdx} className="flex items-start gap-2">
                            <span
                              className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded shrink-0 ${
                                turn.speaker.toLowerCase().includes('ai')
                                  ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                                  : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                              }`}
                            >
                              {turn.speaker}:
                            </span>
                            <p className="text-slate-200 text-xs leading-relaxed">
                              "{turn.text}"
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Interactive Voice Buttons: 'Play Call Recording (Mock)' and 'Trigger Manual Caregiver Callback' */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-1 gap-2">
                      {/* Play Voice Bio with audible SpeechSynthesis & waveform */}
                      <button
                        onClick={() => handleTogglePlayAudio(room)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-2 border shadow-sm ${
                          isAudioPlaying
                            ? 'bg-indigo-600 text-white border-indigo-400 animate-pulse'
                            : 'bg-slate-900 hover:bg-slate-800 text-indigo-300 border-indigo-500/40'
                        }`}
                      >
                        {isAudioPlaying ? (
                          <>
                            <Pause className="h-3.5 w-3.5" />
                            <span>Playing Voice Bio (0:0{playbackSeconds} / 1:45)...</span>
                            <AudioLines className="h-3.5 w-3.5 animate-bounce" />
                          </>
                        ) : (
                          <>
                            <Volume2 className="h-3.5 w-3.5 text-indigo-400" />
                            <span>Play Voice Bio (Morning Wellness Call)</span>
                          </>
                        )}
                      </button>

                      {/* Trigger Manual Caregiver Callback */}
                      <button
                        onClick={() => handleTriggerCaregiverCallback(room)}
                        disabled={isCallbackTriggered}
                        className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500/20 to-teal-500/20 hover:from-emerald-500/30 hover:to-teal-500/30 text-emerald-300 border border-emerald-500/50 text-xs font-bold transition flex items-center gap-1.5 shadow-sm active:scale-95 disabled:opacity-50"
                      >
                        <PhoneForwarded className={`h-3.5 w-3.5 ${isCallbackTriggered ? 'animate-bounce text-emerald-400' : ''}`} />
                        <span>
                          {isCallbackTriggered ? 'Dispatching Intercom Bridge...' : 'Trigger Manual Caregiver Callback'}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Gemini Proactive Intervention SBAR Note (if generated) */}
                {room.aiSbarNote && (
                  <div className="mt-4 p-3.5 rounded-2xl bg-slate-950 border border-teal-500/40 text-xs space-y-2">
                    <div className="flex items-center justify-between text-[11px] font-bold text-teal-300">
                      <span className="flex items-center gap-1.5">
                        <FileCheck className="h-4 w-4 text-teal-400" />
                        <span>Gemini SBAR Handoff & Care Recommendation</span>
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCopySbar(room.roomId, room.aiSbarNote || '')}
                          className="text-[10px] text-teal-300 hover:text-white flex items-center gap-1 font-mono transition"
                        >
                          {copiedSbarRoomId === room.roomId ? <Check className="h-3 w-3 text-emerald-400" /> : <Copy className="h-3 w-3" />}
                          <span>{copiedSbarRoomId === room.roomId ? 'Copied' : 'Copy'}</span>
                        </button>
                        <button
                          onClick={() => handleDispatchSbar(room.roomId)}
                          className="text-[10px] bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 px-2 py-0.5 rounded border border-teal-500/40 font-mono transition"
                        >
                          {dispatchedSbarRoomId === room.roomId ? 'Dispatched ✓' : 'Dispatch to Shift'}
                        </button>
                      </div>
                    </div>
                    <div className="p-2.5 rounded-xl bg-slate-900/90 border border-slate-800 text-[11px] text-slate-200">
                      <NativeMarkdownViewer content={room.aiSbarNote} />
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons: 'Analyze with Gemini' and 'Generate Family SMS' */}
              <div className="mt-5 pt-3 border-t border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs">
                <button
                  onClick={() => handleAnalyzeWithGemini(room)}
                  disabled={analyzingRoomId === room.roomId}
                  className={`px-3.5 py-2 rounded-xl font-bold transition flex items-center justify-center gap-1.5 shadow-md disabled:opacity-50 ${
                    hasActiveAlert
                      ? 'bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400 text-slate-950 shadow-amber-500/20'
                      : 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-slate-950 shadow-teal-500/20'
                  }`}
                  title="Generate SBAR clinical handoff note using Gemini 2.5 Flash"
                >
                  <Sparkles className={`h-3.5 w-3.5 ${analyzingRoomId === room.roomId ? 'animate-spin' : ''}`} />
                  <span>
                    {analyzingRoomId === room.roomId
                      ? 'Analyzing with Gemini...'
                      : hasActiveAlert
                      ? 'Analyze Alert with Gemini (SBAR)'
                      : 'Analyze with Gemini'}
                  </span>
                </button>

                <button
                  onClick={() => handleLaunchFamilySMS(room)}
                  className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-pink-500/20 to-rose-500/20 hover:from-pink-500/30 hover:to-rose-500/30 text-pink-300 border border-pink-500/40 font-semibold transition flex items-center justify-center gap-1.5 shadow-sm"
                  title="Translate live sensor events into warm 3-sentence family update"
                >
                  <MessageSquareHeart className="h-3.5 w-3.5 text-pink-400" />
                  <span>Generate Family SMS</span>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* DEDICATED CARD: Medicare RPM Reimbursement & Cellular Vitals */}
      <div className="mt-8 bg-slate-900/95 border-2 border-cyan-500/50 rounded-3xl p-6 shadow-2xl relative overflow-hidden">
        {/* Glow Accent */}
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

        {/* Card Header Banner */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between pb-5 border-b border-slate-800 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-cyan-500/15 border border-cyan-500/40 text-cyan-300 shadow-lg shadow-cyan-950/40">
              <DollarSign className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-extrabold text-white tracking-wide">
                  Medicare RPM Reimbursement & Cellular Vitals
                </h3>
                <span className="bg-cyan-500/20 text-cyan-300 text-[10px] font-mono font-bold px-2.5 py-0.5 rounded-full border border-cyan-500/40">
                  CPT 99454 COMPLIANT
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                Automated monthly CMS transmission compliance ledger & direct cellular sensor vitals monitoring.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="bg-slate-950 px-4 py-2 rounded-2xl border border-slate-800 text-right">
              <span className="text-[10px] font-mono uppercase text-slate-500 block">CMS Reimbursement Pipeline</span>
              <div className="text-lg font-mono font-black text-emerald-400">
                ${totalReimbursementProjected} / mo
              </div>
            </div>
            <div className="bg-slate-950 px-4 py-2 rounded-2xl border border-slate-800 text-right">
              <span className="text-[10px] font-mono uppercase text-slate-500 block">CPT 99454 Qualified</span>
              <div className="text-lg font-mono font-black text-cyan-300">
                {totalQualifiedCount}/{rooms.length} Residents
              </div>
            </div>
          </div>
        </div>

        {/* Resident-by-Resident Compliance Tracker & Cellular Vital Stream */}
        <div className="mt-5 space-y-4">
          {rooms.map((room) => {
            const rpm = room.rpmTelemetry;
            if (!rpm) return null;

            const progressPct = Math.min(100, (rpm.cpt99454Days / rpm.targetDays) * 100);

            const complianceStatusString = rpm.isQualified
              ? `${room.residentName}: ${rpm.cpt99454Days}/${rpm.targetDays} Days - Qualified for $${rpm.reimbursementAmount} CMS Reimbursement`
              : `${room.residentName}: ${rpm.cpt99454Days}/${rpm.targetDays} Days - ${rpm.daysToThreshold} days to threshold`;

            return (
              <div
                key={`medicare-rpm-${room.roomId}`}
                className={`p-4 rounded-2xl border transition bg-slate-950/80 ${
                  rpm.fluidRetentionAlert
                    ? 'border-amber-500/70 shadow-lg shadow-amber-950/30'
                    : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Left: CPT 99454 Compliance Tracker */}
                  <div className="lg:w-1/2 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <CalendarCheck className="h-4 w-4 text-cyan-400 shrink-0" />
                        <span className="font-extrabold text-white text-sm tracking-wide">
                          {complianceStatusString}
                        </span>
                      </div>
                      <span
                        className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full border shrink-0 ${
                          rpm.isQualified
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                            : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                        }`}
                      >
                        {rpm.isQualified ? 'QUALIFIED' : 'PENDING'}
                      </span>
                    </div>

                    {/* Progress Bar & Subtitle */}
                    <div className="space-y-1">
                      <div className="w-full bg-slate-900 h-2.5 rounded-full overflow-hidden border border-slate-800">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            rpm.isQualified
                              ? 'bg-gradient-to-r from-emerald-500 to-teal-400'
                              : 'bg-amber-400'
                          }`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-[11px] font-mono text-slate-400">
                        <span>
                          {room.roomNumber} ({room.wing})
                        </span>
                        <span className="flex items-center gap-1 text-slate-400">
                          <Signal className="h-3 w-3 text-cyan-400" />
                          <span>Last Cellular Upload: {rpm.lastTransmissionTime}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right: Cellular RPM Vitals (Cellular Blood Pressure & 48h Weight Delta) */}
                  <div className="lg:w-1/2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Cellular Blood Pressure */}
                    <div className="p-3 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-between">
                      <div className="space-y-0.5">
                        <span className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1.5 font-bold">
                          <Heart className="h-3.5 w-3.5 text-rose-400" />
                          <span>Cellular Blood Pressure</span>
                        </span>
                        <div className="text-sm font-mono font-black text-white">
                          {rpm.cellularBP}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-[10px] font-mono text-slate-500 block">Pulse</span>
                        <span className="text-xs font-mono font-bold text-rose-300">
                          {rpm.pulseBpm} bpm
                        </span>
                      </div>
                    </div>

                    {/* Cellular Scale Weight & 48h Delta */}
                    <div
                      className={`p-3 rounded-2xl border flex items-center justify-between ${
                        rpm.fluidRetentionAlert
                          ? 'bg-amber-950/40 border-amber-500/80 text-amber-200'
                          : 'bg-slate-900 border-slate-800'
                      }`}
                    >
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1.5 font-bold">
                            <Scale className="h-3.5 w-3.5 text-cyan-400" />
                            <span>Cellular Scale</span>
                          </span>
                          {rpm.fluidRetentionAlert && (
                            <span className="bg-amber-400 text-slate-950 text-[9px] font-black px-1.5 rounded">
                              ALERT
                            </span>
                          )}
                        </div>
                        <div className="text-sm font-mono font-black text-white">
                          {rpm.weightLbs} lbs
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-[10px] font-mono text-slate-400 block">48h Delta</span>
                        <span
                          className={`text-xs font-mono font-black ${
                            rpm.fluidRetentionAlert
                              ? 'text-amber-400 text-sm font-extrabold animate-pulse'
                              : 'text-slate-200'
                          }`}
                        >
                          {rpm.weightDelta48hLbs > 0 ? `+${rpm.weightDelta48hLbs}` : rpm.weightDelta48hLbs} lbs
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Fluid Retention Alert Banner if delta spike is detected */}
                {rpm.fluidRetentionAlert && (
                  <div className="mt-3.5 p-3 rounded-xl bg-amber-950/30 border border-amber-500/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs">
                    <div className="flex items-center gap-2 text-amber-300 font-semibold">
                      <AlertCircle className="h-4 w-4 text-amber-400 shrink-0" />
                      <span>
                        Cellular Scale: <strong>+{rpm.weightDelta48hLbs} lbs</strong> in 48h (Fluid Retention Alert). Recommend clinical diuretic & cardiopulmonary assessment.
                      </span>
                    </div>

                    <button
                      onClick={() => handleAnalyzeWithGemini(room)}
                      className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/50 font-bold transition flex items-center gap-1 text-[11px] self-start sm:self-auto shrink-0"
                    >
                      <Sparkles className="h-3 w-3 text-amber-300" />
                      <span>Assess Retention with Gemini</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Aqara mmWave Radar Hardware Bridge & Multi-Sensor Pairing Modal */}
      <AqaraHardwareModal
        isOpen={isAqaraModalOpen}
        onClose={() => setIsAqaraModalOpen(false)}
        rooms={rooms}
        initialTab={aqaraModalTab}
        initialRoomId={selectedCalibratedRoomId}
        onTriggerHardwareFall={(roomId) => {
          playAlertChime();
          if (onTriggerHardwareFall) {
            onTriggerHardwareFall(roomId);
          } else {
            onSimulateFall();
          }
        }}
        onResetHardwareAlert={onResetSimulation}
      />

      {/* Life-Safety Telephony & SMS Escalation Modal */}
      <EmergencySmsModal
        isOpen={isSmsModalOpen}
        onClose={() => setIsSmsModalOpen(false)}
        currentPhone={emergencyPhone}
        onUpdatePhone={(newPhone) => {
          setEmergencyPhone(newPhone);
          localStorage.setItem('medicore_emergency_phone', newPhone);
        }}
      />
    </div>
  );
};
